import React, { useRef, useEffect, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Form, Typography, Input, Select, theme, Spin, Col, Grid, Tooltip, Flex } from 'antd';
import _ from 'lodash';

import { MessageContext } from '../../../layouts/NavigationLayout';

//Constants
import CommonEnum from '../../../constants/CommonEnum';

//Helpers
import GeneralHelper from '../../../helpers/general';

//Components
import Confirmation from '../../../components/Confirmation';
import ButtonGroupComponent from './ButtonGroupComponent';


const { Title, Text } = Typography;
const { Item } = Form;
const { TextArea } = Input;
const { useToken } = theme;
const { useBreakpoint } = Grid;

// eslint-disable-next-line no-control-regex
const nonEnglishRegex = /[^\x00-\x7F]+/;

const ApplicationInformationView = (props) => {

	const [nextDisabled, setNextDisabled] = useState(true);
	const [draftDisabled, setDraftDisabled] = useState(true);
	const [isNext, setIsNext] = useState(false);
	const [programFirst, setProgramFirst] = useState(null);
	const [programSecond, setProgramSecond] = useState(null);
	const [programThird, setProgramThird] = useState(null);
	const [reasonWordCount, setReasonWordCount] = useState(0);
	const [maxCountExceeded, setMaxCountExceeded] = useState(false);
	const [warningMessage, setWarningMessage] = useState(null);
	const [confirmation, setConfirmation] = useState(false);
	const [isInitialLoad, setIsInitialLoad] = useState(true);

	const history = useHistory();
	const formRef = useRef(null);
	const { token } = useToken();
	const screens = useBreakpoint();
	const messageApi = useContext(MessageContext);

	const query = GeneralHelper.getParamsFromUrl();

	const { colorPrimary } = token;

	const { applicationInfoPageProps, documentPageProps, getApplicationInfo, getApplication, onApplicationInfoSubmit,
		resetApplicationInfoPage, resetApplicationInfoMeta, reviewAndSubmissionPageProps, setHasUnsavedChanges, resetDocumentMeta } = props;
	const { applicationInfo, loading, loadingSubmit, meta } = applicationInfoPageProps || {};
	const { meta: documentMeta } = documentPageProps || {};

	const {
		program_first_choice,
		program_second_choice,
		program_third_choice,
		other_program_first_choice,
		other_program_second_choice,
		other_program_third_choice,
		reason
	} = applicationInfo || {};
	const { application } = reviewAndSubmissionPageProps;
	const currentStep = _.get(application, 'current_step');

	useEffect(() => {
		query.application_id && getApplicationInfo(query.application_id);
		return () => {
			resetApplicationInfoPage();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (applicationInfo) {
			const wordCount = reason ? GeneralHelper.countWords(reason) : 0;
			const exceeded = wordCount >= 400 ? true : false;
			setNextDisabled(false);
			setProgramFirst(program_first_choice);
			setProgramSecond(program_second_choice);
			setProgramThird(program_third_choice);
			setReasonWordCount(wordCount);
			setMaxCountExceeded(exceeded);
		} else {
			setNextDisabled(true);
			setProgramFirst(null);
			setProgramSecond(null);
			setProgramThird(null);
			setReasonWordCount(0);
			setMaxCountExceeded(false);
		}
		setIsInitialLoad(true);
		checkFormValues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [applicationInfo]);

	useEffect(() => {
		if (meta) {
			setIsInitialLoad(true);
			if (meta.code === 200) {
				setDraftDisabled(true);
				setHasUnsavedChanges(false);
				setWarningMessage(null);
				if (isNext) {
					if (currentStep === 6) {
						window.location.href = process.env.NODE_ENV === 'development'
							? `/application?step=6&application_id=${query.application_id}`
							: `/summercamp/app/application?step=6&application_id=${query.application_id}`;
					} else {
						history.push(`/application?step=4&application_id=${query.application_id}`);
					}
				}
				!isNext && isInitialLoad === false && messageApi.open({
					type: 'success',
					content: `${meta.message}`,
					duration: 2.5
				});
				if (applicationInfo) {
					const wordCount = reason ? GeneralHelper.countWords(reason) : 0;
					const exceeded = wordCount >= 400 ? true : false;
					setReasonWordCount(wordCount);
					setMaxCountExceeded(exceeded);
				} else {
					setReasonWordCount(0);
					setMaxCountExceeded(false);
				}
				!isNext && !nextDisabled && query.application_id && getApplication(query.application_id);
				query.application_id && getApplicationInfo(query.application_id);
			} else {
				messageApi.open({
					type: 'error',
					content: `${meta.message}`,
					duration: 2.5
				});
			}
		}
		return () => {
			resetApplicationInfoMeta();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meta]);

	useEffect(() => {
		if (documentMeta) {
			if (documentMeta.code === 200) {
				messageApi.open({
					type: 'success',
					content: `${documentMeta.message}`,
					duration: 2.5
				});
			} else {
				messageApi.open({
					type: 'error',
					content: `${documentMeta.message}`,
					duration: 2.5
				});
			}
		}
		return () => {
			resetDocumentMeta();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentMeta]);

	useEffect(() => {
		if (reasonWordCount <= 400) {
			setMaxCountExceeded(false);
			setWarningMessage(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reasonWordCount]);

	useEffect(() => {
		checkFormValues();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInitialLoad]);

	//Start of Functions

	const onNext = () => {
		setIsNext(true);
	};

	const checkFormValues = () => {
		if (formRef.current) {
			if (
				(formRef.current.getFieldValue('program_first_choice') === 'Others' ? (
					formRef.current.getFieldValue('other_program_first_choice')
				) : formRef.current.getFieldValue('program_first_choice')) &&
				(formRef.current.getFieldValue('program_second_choice') === 'Others' ? (
					formRef.current.getFieldValue('other_program_second_choice')
				) : formRef.current.getFieldValue('program_second_choice')) &&
				(formRef.current.getFieldValue('program_third_choice') === 'Others' ? (
					formRef.current.getFieldValue('other_program_third_choice')
				) : formRef.current.getFieldValue('program_third_choice') || !formRef.current.getFieldValue('program_third_choice')) &&
				formRef.current.getFieldValue('reason')
			) {
				setNextDisabled(false);
			} else {
				setNextDisabled(true);
			}

			//Separate trappings for draftDisabled
			const firstChoiceIsValid = formRef.current.getFieldValue('program_first_choice') === 'Others' ?
				(!_.isNil(formRef.current.getFieldValue('other_program_first_choice')) ? true : false) : true;
			const secondChoiceIsValid = formRef.current.getFieldValue('program_second_choice') === 'Others' ?
				(!_.isNil(formRef.current.getFieldValue('other_program_second_choice')) ? true : false) : true;
			const thirdChoiceIsValid = formRef.current.getFieldValue('program_third_choice') === 'Others' ?
				(!_.isNil(formRef.current.getFieldValue('other_program_third_choice') &&
					formRef.current.getFieldValue('program_third_choice') !== 'N/A') ? true : false) : true;
			if (
				firstChoiceIsValid &&
				secondChoiceIsValid &&
				thirdChoiceIsValid &&
				(formRef.current.getFieldValue('program_first_choice') ||
					formRef.current.getFieldValue('program_second_choice') ||
					formRef.current.getFieldValue('program_third_choice') ||
					formRef.current.getFieldValue('reason')
				)
			) {
				if (isInitialLoad) {
					setDraftDisabled(true);
					setHasUnsavedChanges(false);
				} else {
					setDraftDisabled(false);
					setHasUnsavedChanges(true);
				}
			} else {
				setDraftDisabled(true);
				setHasUnsavedChanges(false);
			}
		}
	};

	const onPaste = (event) => {
		const maxWordCount = 400;
		const remainingWords = maxWordCount - reasonWordCount;

		if (remainingWords <= 0) {
			event.preventDefault();
			setMaxCountExceeded(true);
			const warningMessage = 'Maximum word count limit reached.';
			setWarningMessage(warningMessage);
			return;
		}

		const clipboardData = event.clipboardData || window.clipboardData || event.originalEvent.clipboardData;
		const pastedText = clipboardData.getData('text/plain');
		const pastedWords = pastedText.trim().split(/\s+/);

		if (pastedWords.length > remainingWords) {
			event.preventDefault();
			setMaxCountExceeded(true);
			const warningMessage = `You can only paste up to ${remainingWords} more word(s).`;
			setWarningMessage(warningMessage);
		} else {
			setMaxCountExceeded(false);
			setIsInitialLoad(false);
		}
	};

	const renderOptionLabel = (label) => {
		if (screens.xs && label.length > 25) {
			return (
				<Tooltip
					autoAdjustOverflow={true}
					placement="top"
					title={label}
					key={label}
				>
					{GeneralHelper.truncateLongText(label, 25)}
				</Tooltip>
			);
		}
		return label;
	};

	//End of Functions

	if (loading) {
		return (
			<div
				style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
			>
				<Spin />
			</div>
		);
	}

	return (
		<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
			<Row>
				<Col flex={1}>
					<Form
						ref={formRef}
						layout="vertical"
						initialValues={{
							program_first_choice,
							program_second_choice,
							program_third_choice: applicationInfo && _.isNil(program_third_choice) ? 'N/A' : program_third_choice,
							other_program_first_choice,
							other_program_second_choice,
							other_program_third_choice,
							reason
						}}
						onFinish={(values) => {
							const fields = {
								application_id: query.application_id,
								save_method: nextDisabled ? 'draft_save' : 'final_save',
								...values
							};

							if (applicationInfo) {
								fields.updated_at = applicationInfo.updated_at;
								onApplicationInfoSubmit({ id: applicationInfo._id, fields });
							} else {
								onApplicationInfoSubmit({ fields });
							}
						}}

					>
						<Title
							level={2}
							style={{ color: colorPrimary }}
						>Step 4</Title>
						<Item>
							<Text style={{ fontSize: 20, fontWeight: 510 }}>Application Information</Text>
						</Item>
						<Item
							label="Program of Interest (First Choice)"
							name="program_first_choice"
							required
						>
							<Select
								placeholder="Select First Choice"
								options={CommonEnum.program_of_interest.map((program, index) => ({
									value: program.value,
									label: renderOptionLabel(program.label),
									disabled: program.value !== 'Others' ? (programSecond === program.value ||
										programThird === program.value || program.value === 'N/A') : false
								}))}
								onChange={(value) => {
									setProgramFirst(value);
									setWarningMessage(null);
									setIsInitialLoad(false);
									checkFormValues();
								}}
								style={{ width: '100%', maxWidth: '100%' }}
							/>
						</Item>
						{programFirst === 'Others' &&
							<Item
								label="Other Program of Interests (First Choice)"
								name="other_program_first_choice"
								required
							>
								<Input
									onChange={(e) => {
										const inputValue = e.target.value;

										// Check if it contains disallowed characters
										if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
											// Sanitize the input by removing disallowed characters
											const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

											// Update form with sanitized value
											formRef.current.setFieldsValue({ other_program_first_choice: sanitizedValue });
										}

										// After sanitizing, proceed with your other operations
										setIsInitialLoad(false);
										checkFormValues();
									}}
									onKeyDown={GeneralHelper.preventDisallowedKeys}
									placeholder="Please State"
									autoComplete="nope"
								/>
							</Item>
						}
						<Item
							label="Program of Interest (Second Choice)"
							name="program_second_choice"
							required
						>
							<Select
								placeholder="Select Second Choice"
								options={CommonEnum.program_of_interest.map(program => ({
									value: program.value,
									label: renderOptionLabel(program.label),
									disabled: program.value !== 'Others' ? (programFirst === program.value ||
										programThird === program.value || program.value === 'N/A') : false
								}))}
								onChange={(value) => {
									setProgramSecond(value);
									setWarningMessage(null);
									setIsInitialLoad(false);
									checkFormValues();
								}}
							/>
						</Item>
						{programSecond === 'Others' &&
							<Item
								label="Other Program of Interests (Second Choice)"
								name="other_program_second_choice"
								required
							>
								<Input
									onChange={(e) => {
										const inputValue = e.target.value;

										// Check if it contains disallowed characters
										if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
											// Sanitize the input by removing disallowed characters
											const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

											// Update form with sanitized value
											formRef.current.setFieldsValue({ other_program_second_choice: sanitizedValue });
										}

										// After sanitizing, proceed with your other operations
										setIsInitialLoad(false);
										checkFormValues();
									}}
									onKeyDown={GeneralHelper.preventDisallowedKeys}
									placeholder="Please State"
									autoComplete="nope"
								/>
							</Item>
						}
						<Item
							label="Program of Interest (Third Choice)"
							name="program_third_choice"
						>
							<Select
								placeholder="Select Third Choice"
								options={CommonEnum.program_of_interest.map(program => ({
									value: program.value,
									label: renderOptionLabel(program.label),
									disabled: program.value !== 'Others' ? programFirst === program.value || programSecond === program.value : false
								}))}
								onChange={(value) => {
									setProgramThird(value);
									setWarningMessage(null);
									setIsInitialLoad(false);
									checkFormValues();
								}}
								onClear={() => {
									setIsInitialLoad(false);
									checkFormValues();
								}}
								allowClear
							/>
						</Item>
						{programThird === 'Others' &&
							<Item
								label="Other Program of Interests (Third Choice)"
								name="other_program_third_choice"
								required
							>
								<Input
									onChange={(e) => {
										const inputValue = e.target.value;

										// Check if it contains disallowed characters
										if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
											// Sanitize the input by removing disallowed characters
											const sanitizedValue = GeneralHelper.sanitizeInput(inputValue);

											// Update form with sanitized value
											formRef.current.setFieldsValue({ other_program_third_choice: sanitizedValue });
										}

										// After sanitizing, proceed with your other operations
										setIsInitialLoad(false);
										checkFormValues();
									}}
									onKeyDown={GeneralHelper.preventDisallowedKeys}
									placeholder="Please State"
									autoComplete="nope"
								/>
							</Item>
						}
						<Item
							label="Reason(s) for applying to the Summer Camp
					(A maximum of 400 words in English)"
							name="reason"
							required
						>
							<TextArea
								placeholder="Enter reason(s) of application"
								rows={5}
								onChange={(e) => {
									let inputValue = e.target.value;
									let needsUpdate = false;

									// Check for non-English characters first
									if (nonEnglishRegex.test(inputValue)) {
										const characters = inputValue.split('');
										let newValue = '';
										characters.forEach(char => {
											if (!nonEnglishRegex.test(char)) {
												newValue += char;
											}
										});

										inputValue = newValue;
										needsUpdate = true;
									}

									// Check for disallowed characters
									if (GeneralHelper.checkDisallowedCharacters(inputValue)) {
										inputValue = GeneralHelper.sanitizeInput(inputValue);
										needsUpdate = true;
									}

									// Update form value if either sanitization was applied
									if (needsUpdate) {
										formRef.current?.setFieldValue('reason', inputValue);
									}

									// Continue with existing functionality
									setIsInitialLoad(false);
									checkFormValues();

									if (inputValue) {
										const wordCount = GeneralHelper.countWords(inputValue);
										setReasonWordCount(wordCount);
									} else {
										setReasonWordCount(0);
									}
								}}
								onKeyPress={(e) => {
									if (reasonWordCount >= 400 && (e.key === ' ' || e.keyCode === 32)) {
										e.preventDefault();
										setMaxCountExceeded(true);
										const warningMessage = 'Maximum word count limit reached.';
										setWarningMessage(warningMessage);
									} else {
										setMaxCountExceeded(false);
										setWarningMessage(null);
										setIsInitialLoad(false);
									}
								}}
								onKeyDown={GeneralHelper.preventDisallowedKeys}
								onPaste={onPaste}
								autoComplete="nope"
							/>
						</Item>
						<Row style={{ marginBottom: 10, marginTop: -10 }}>
							<Col span={12}>
								{maxCountExceeded && <Text type="warning">{warningMessage}</Text>}
							</Col>
							<Col
								span={12}
								align="end"
							>
								<Text>{reasonWordCount}/400</Text>
							</Col>
						</Row>
						<ButtonGroupComponent
							currentStep={currentStep}
							draftDisabled={draftDisabled}
							saveDisabled={nextDisabled}
							loadingSubmit={loadingSubmit}
							onSave={onNext}
							onPrevious={() => {
								if (!draftDisabled) {
									setConfirmation(true);
								} else {
									history.push(`/application?step=2&application_id=${query.application_id}`);
								}
							}}
						/>
						<Confirmation
							loading={loadingSubmit}
							open={confirmation}
							onCancel={() => setConfirmation(false)}
							onOk={() => {
								draftDisabled === false && formRef.current.submit();
								history.push(`/application?step=2&application_id=${query.application_id}`);
							}}
						>
							<Flex
								vertical
								gap={30}
							>
								<Text>
									Save your changes before going back to the previous step?
								</Text>
							</Flex>
						</Confirmation>
					</Form>
				</Col>
			</Row>
		</div>
	);
};

export default ApplicationInformationView;
